import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export enum NudgeType {
  BOTTOM = 'bottom',
  TOPBAR = 'topbar',
  NONE = 'none'
}

@Injectable({
  providedIn: 'root'
})
export class NudgesService {
  // Bottom nudge (bottom banner) management
  private readonly bottomNudgeVisibleSubject = new BehaviorSubject<boolean>(false);
  public bottomNudgeVisible$ = this.bottomNudgeVisibleSubject.asObservable();
  
  private readonly bottomNudgeDismissedSubject = new BehaviorSubject<boolean>(false);
  public bottomNudgeDismissed$ = this.bottomNudgeDismissedSubject.asObservable();
  
  // Topbar nudge management
  private readonly topbarNudgeVisibleSubject = new BehaviorSubject<boolean>(false);
  public topbarNudgeVisible$ = this.topbarNudgeVisibleSubject.asObservable();

  // Local storage keys
  private readonly BOTTOM_NUDGE_DISMISSED_KEY = 'bottom_nudge_dismissed';
  private readonly TOPBAR_NUDGE_DISMISSED_KEY = 'topbar_nudge_dismissed_session';

  constructor() {}

  // ===== BOTTOM NUDGE (BOTTOM BANNER) METHODS =====
  
  /**
   * Show the bottom app download nudge (bottom banner)
   */
  showBottomNudge(): void {
    this.bottomNudgeVisibleSubject.next(true);
  }

  /**
   * Mark bottom nudge as dismissed permanently
   */
  dismissBottomNudge(): void {
    this.bottomNudgeVisibleSubject.next(false);
    this.bottomNudgeDismissedSubject.next(true);
    localStorage.setItem(this.BOTTOM_NUDGE_DISMISSED_KEY, 'true');
  }

  /**
   * Check if bottom nudge should be shown
   */
  shouldShowBottomNudge(): boolean {
    const bottomNudgeDismissed = localStorage.getItem(this.BOTTOM_NUDGE_DISMISSED_KEY) === 'true';
    return !bottomNudgeDismissed;
  }

  // ===== TOPBAR NUDGE METHODS =====
  
  /**
   * Show the topbar nudge
   */
  showTopbarNudge(): void {
    this.topbarNudgeVisibleSubject.next(true);
  }

  /**
   * Mark topbar nudge as dismissed for this session
   */
  dismissTopbarNudge(): void {
    this.topbarNudgeVisibleSubject.next(false);
    sessionStorage.setItem(this.TOPBAR_NUDGE_DISMISSED_KEY, 'true');
  }

  /**
   * Check if topbar nudge should be shown
   */
  shouldShowTopbarNudge(): boolean {
    const bottomNudgeDismissed = localStorage.getItem(this.BOTTOM_NUDGE_DISMISSED_KEY) === 'true';
    const topbarNudgeDismissed = sessionStorage.getItem(this.TOPBAR_NUDGE_DISMISSED_KEY) === 'true';
    
    return bottomNudgeDismissed && !topbarNudgeDismissed;
  }

  // ===== COMPOSITE METHODS =====
  
  /**
   * Determine which nudge should be shown based on current state
   */
  determineNudgeToShow(): NudgeType {
    if (this.shouldShowBottomNudge()) {
      return NudgeType.BOTTOM;
    } else if (this.shouldShowTopbarNudge()) {
      return NudgeType.TOPBAR;
    } else {
      return NudgeType.NONE;
    }
  }
} 